var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React from 'react';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { tooltipClasses } from '@mui/material/Tooltip';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { baseColors } from '../../../../Themes/colors';
export var SOURCE_DOCUMENT_LABELS = {
    documentPackage: 'Doc Package',
    invoice: 'Invoice',
    driversLicense: "Driver's License",
};
var IconCircle = function (_a) {
    var children = _a.children, _b = _a.attention, attention = _b === void 0 ? false : _b;
    return (React.createElement(Box, { sx: {
            width: 32,
            height: 32,
            borderRadius: '50%',
            backgroundColor: attention ? baseColors.errorPreset[40] : baseColors.greyPreset[55],
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            '& svg, & span': { fontSize: 16, color: baseColors.darkPreset[10] },
        } }, children));
};
export var ReviewTooltip = styled(function (_a) {
    var className = _a.className, props = __rest(_a, ["className"]);
    return (React.createElement(Tooltip, __assign({}, props, { arrow: true, classes: { popper: className }, placement: "top" })));
})(function () {
    var _a;
    return (_a = {},
        _a["& .".concat(tooltipClasses.tooltip)] = {
            backgroundColor: baseColors.bluePreset[10],
            color: baseColors.fullWhite,
            borderRadius: 8,
            boxShadow: '0 10px 24px rgba(0,0,0,0.24)',
            padding: '14px 18px',
            minWidth: 250,
            maxWidth: 'none',
        },
        _a["& .".concat(tooltipClasses.arrow)] = {
            color: baseColors.bluePreset[10],
        },
        _a);
});
export var TooltipBlock = function (_a) {
    var header = _a.header, lines = _a.lines;
    return (React.createElement(Stack, { direction: "row", gap: 1.25, alignItems: "flex-start" },
        React.createElement(InfoOutlinedIcon, { sx: { fontSize: 16, color: baseColors.fullWhite, flexShrink: 0, mt: 0.2 } }),
        React.createElement(Stack, { direction: "column", gap: 0.5 },
            React.createElement(Typography, { variant: "caption", fontWeight: 700, fontSize: 13, color: baseColors.fullWhite, lineHeight: 1.4 }, header),
            lines.map(function (line, index) { return (React.createElement(Typography, { key: index, variant: "caption", color: baseColors.fullWhite, lineHeight: 1.6 }, line)); }))));
};
var FieldBadge = function (_a) {
    var tone = _a.tone, tooltip = _a.tooltip;
    var isMatched = tone === 'matched';
    var badge = (React.createElement(Stack, { direction: "row", alignItems: "center", gap: 0.5, sx: {
            borderRadius: '6px',
            px: 0.75,
            py: 0.25,
            flexShrink: 0,
            backgroundColor: isMatched ? baseColors.successPreset[60] : baseColors.greyPreset[55],
            border: "1px solid ".concat(isMatched ? baseColors.successPreset[70] : baseColors.greyPreset[40]),
        } },
        isMatched ? (React.createElement("span", { className: "icon-Circle_Check", style: { fontSize: 12, color: baseColors.successPreset[30] } })) : (React.createElement("span", { className: "icon-Warning", style: { fontSize: 12, color: baseColors.errorPreset[10] } })),
        React.createElement(Typography, { variant: "caption", fontWeight: 500, sx: { color: isMatched ? baseColors.successPreset[50] : baseColors.darkPreset[10] } }, isMatched ? 'Matched' : 'Review')));
    return tooltip ? React.createElement(ReviewTooltip, { title: tooltip }, badge) : badge;
};
var SourceLink = function (_a) {
    var label = _a.label, onOpen = _a.onOpen;
    return (React.createElement(Stack, { direction: "row", alignItems: "center", gap: 0.4, onClick: onOpen, sx: {
            flexShrink: 0,
            cursor: onOpen ? 'pointer' : 'default',
            '&:hover .source-link-label': onOpen ? { textDecoration: 'underline' } : undefined,
        } },
        React.createElement(Typography, { className: "source-link-label", variant: "caption", fontWeight: 600, color: baseColors.successPreset[30] }, label),
        React.createElement("span", { className: "icon-Documents", style: { fontSize: 13, color: baseColors.successPreset[30] } })));
};
var OverviewFieldMatchSection = function (_a) {
    var id = _a.id, title = _a.title, icon = _a.icon, label = _a.label, systemValue = _a.systemValue, badgeTone = _a.badgeTone, reason = _a.reason, sourceEntries = _a.sourceEntries, _b = _a.onSourceLabel, onSourceLabel = _b === void 0 ? 'Source' : _b, onSourceOpen = _a.onSourceOpen, _c = _a.actionLabel, actionLabel = _c === void 0 ? 'Update' : _c;
    var tooltipBlocks = sourceEntries.length > 0
        ? sourceEntries.map(function (entry) { return ({
            header: "".concat(entry.sourceLabel.toUpperCase(), " ").concat(entry.mismatched ? 'REVIEW' : 'MATCH'),
            lines: __spreadArray(["System: ".concat(systemValue), "".concat(entry.sourceLabel, ": ").concat(entry.value)], (entry.reason ? [entry.reason] : []), true),
        }); })
        : reason
            ? [{ header: badgeTone === 'matched' ? 'MATCH' : 'REVIEW', lines: [reason] }]
            : [];
    var badgeTooltip = tooltipBlocks.length > 0 ? (React.createElement(Stack, { gap: 1.5, sx: { maxWidth: 280 } }, tooltipBlocks.map(function (block, index) { return (React.createElement(TooltipBlock, { key: index, header: block.header, lines: block.lines })); }))) : undefined;
    return (React.createElement(Stack, { gap: 1, id: id, sx: { scrollMarginTop: 8 } },
        React.createElement(Typography, { variant: "subtitle1", fontWeight: 700, color: baseColors.darkPreset[10] }, title),
        React.createElement(Box, { sx: {
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
                gap: 2,
                alignItems: 'stretch',
            } },
            React.createElement(Stack, { sx: {
                    border: "1px solid ".concat(baseColors.greyPreset[40]),
                    borderRadius: 2,
                    backgroundColor: baseColors.fullWhite,
                    overflow: 'hidden',
                } },
                React.createElement(Stack, { direction: "row", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, sx: { p: 1.5 } },
                    React.createElement(Stack, { direction: "row", gap: 1.5, sx: { minWidth: 0 } },
                        React.createElement(IconCircle, null, icon),
                        React.createElement(Stack, { gap: 0.25, sx: { minWidth: 0 } },
                            React.createElement(Typography, { variant: "caption", color: baseColors.greyPreset[30] }, label),
                            React.createElement(Typography, { variant: "body2", fontWeight: 500, color: baseColors.greyPreset[10] }, systemValue))),
                    React.createElement(FieldBadge, { tone: badgeTone, tooltip: badgeTooltip })),
                React.createElement(Box, { sx: { borderTop: "1px solid ".concat(baseColors.greyPreset[40]) } }),
                React.createElement(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", sx: { px: 1.5, py: 1 } },
                    React.createElement(SourceLink, { label: onSourceLabel, onOpen: onSourceOpen }),
                    React.createElement(Typography, { variant: "caption", fontWeight: 600, color: baseColors.successPreset[30], sx: { cursor: 'pointer' } }, actionLabel))),
            React.createElement(Stack, { sx: {
                    border: "1px solid ".concat(baseColors.greyPreset[40]),
                    borderRadius: 2,
                    backgroundColor: baseColors.fullWhite,
                    overflow: 'hidden',
                    justifyContent: sourceEntries.length === 1 ? 'center' : 'flex-start',
                } }, sourceEntries.map(function (entry, index) { return (React.createElement(React.Fragment, { key: "".concat(entry.sourceLabel, "-").concat(index) },
                index > 0 && React.createElement(Box, { sx: { borderTop: "1px solid ".concat(baseColors.greyPreset[40]) } }),
                React.createElement(Stack, { direction: "row", alignItems: "flex-start", justifyContent: "space-between", gap: 1.5, sx: { p: 1.5 } },
                    React.createElement(Stack, { direction: "row", gap: 1.5, sx: { minWidth: 0 } },
                        React.createElement(IconCircle, { attention: entry.mismatched }, entry.icon),
                        React.createElement(Stack, { gap: 0.25, sx: { minWidth: 0 } },
                            React.createElement(Typography, { variant: "caption", color: baseColors.greyPreset[30] }, entry.label),
                            React.createElement(Typography, { variant: "body2", fontWeight: 500, color: baseColors.greyPreset[10] }, entry.value))),
                    React.createElement(SourceLink, { label: entry.sourceLabel, onOpen: entry.onOpen })))); })))));
};
export default OverviewFieldMatchSection;
